import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  useToast,
} from '@lead-magnifier/ui'
import { ArrowLeft, Sparkles } from 'lucide-react'
import { useCreateEvent } from '@/hooks/use-events'

export const Route = createFileRoute('/_authenticated/events/new')({
  component: NewEventPage,
})

function NewEventPage() {
  const navigate = useNavigate()
  const createEvent = useCreateEvent()
  const { toast } = useToast()

  // Initialize with today and next week as defaults
  const today = new Date().toISOString().split('T')[0]
  const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  const [name, setName] = useState('')
  const [downloadUrl, setDownloadUrl] = useState('')
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(nextWeek)

  // Auto-generate slug from name
  const slug = name
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' })[c] || c)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name || !startDate || !endDate) {
      toast({
        title: 'Fehlende Felder',
        description: 'Bitte fülle alle Pflichtfelder aus.',
        variant: 'destructive',
      })
      return
    }

    try {
      const event = await createEvent.mutateAsync({
        name,
        slug,
        downloadUrl: downloadUrl || 'https://example.com/download',
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
      })

      toast({
        title: 'Event erstellt!',
        description: 'Dein Event wurde erfolgreich erstellt.',
      })

      navigate({ to: `/events/${event.id}` })
    } catch (error) {
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Event konnte nicht erstellt werden.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Link to="/events" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zu Events
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Neues Event erstellen</CardTitle>
              <CardDescription>
                Erstelle ein neues Event mit eigenem QR-Code
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {/* Event Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Event-Name *</Label>
              <Input
                id="name"
                placeholder="z.B. Messe Innsbruck 2025"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {slug && (
                <p className="text-xs text-muted-foreground">
                  URL: /form/.../<span className="font-mono">{slug}</span>
                </p>
              )}
            </div>

            {/* Dates */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="startDate">Startdatum *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">Enddatum *</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Download URL */}
            <div className="space-y-2">
              <Label htmlFor="downloadUrl">
                Download-URL <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="downloadUrl"
                type="url"
                placeholder="https://example.com/whitepaper.pdf"
                value={downloadUrl}
                onChange={(e) => setDownloadUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Link zur Datei, die Besucher nach der Bestätigung erhalten.
                Kann später in den Event-Einstellungen geändert werden.
              </p>
            </div>
          </CardContent>

          <CardFooter className="flex justify-between border-t pt-6">
            <Link to="/events">
              <Button type="button" variant="ghost">
                Abbrechen
              </Button>
            </Link>
            <Button type="submit" disabled={createEvent.isPending}>
              {createEvent.isPending ? 'Wird erstellt...' : 'Event erstellen'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
